import React, { FC } from 'react';
import { Col, Row, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';

import { Employee } from '~/types';

import { usePath, usePermissions } from '~/hooks';
import { employees } from '~/routes';
import { StarIcon, ShieldCheckmarkIcon } from '~/img';
import { usePageShow } from '~/hooks/usePageQuery';
import { useIdentity } from '~/hooks/identity';
import { useEmployeesRoles } from '~/hooks/employees';
import { getEmployeeRole } from '~/utils/getEmployeeRole';
import { getRoleColor } from '~/utils/employees';

interface Props {
  data: Employee[] | undefined;
  total: number;
  loading: boolean;
}

const EmployeesTable: FC<Props> = (props) => {
  const { data, loading, total } = props;
  const { pagination } = usePath();
  const { showOpen } = usePageShow(employees);
  const { data: identityData } = useIdentity();
  const { data: employeeRoles } = useEmployeesRoles();

  const { t } = useTranslation('employees');
  const dt = (key: string) => t(`translation:${key}`);
  const columns: ColumnsType<Employee> = [
    {
      title: dt('name'),
      dataIndex: 'firstName',
      render: (_, record) => {
        const { firstName, lastName, email } = record;
        return (
          <Space size={4}>
            <div>{`${firstName || ''} ${lastName || ''} ${
              email === identityData?.emailAddress ? `(${dt('you')})` : ''
            }`}</div>
            <EmployeeIconIndicators record={record} table />
          </Space>
        );
      },
    },
    {
      title: t('roles'),
      dataIndex: 'roles',
      render: (_, record) => {
        const roles = record.roles || [];
        return (
          <Row gutter={[0, 8]} style={tagRowStyle}>
            {[...roles]
              ?.sort((a, b) => (a > b ? 1 : -1))
              .map((item: string) => (
                <Col key={item}>
                  <Tag color={getRoleColor(item)}>{getEmployeeRole(item, employeeRoles)}</Tag>
                </Col>
              ))}
          </Row>
        );
      },
    },
    {
      title: t('position'),
      dataIndex: ['authority', 'position'],
    },
  ];

  const hasAccessView = usePermissions('business.employee.view');

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey={(record) => record.id as string}
      rowClassName="cursor-pointer"
      pagination={{ ...pagination, total }}
      onRow={(record) => ({
        onClick: () => hasAccessView && showOpen(record.id as string),
      })}
    />
  );
};

const tagRowStyle = { marginBottom: -4 };

interface EmployeeIconIndicatorsProps {
  record: Employee;
  table?: boolean;
}

export const EmployeeIconIndicators: FC<EmployeeIconIndicatorsProps> = (props) => {
  const { record, table } = props;
  const { t } = useTranslation('employees');

  return (
    <>
      {record.authority?.owner && (
        <Tooltip title={t('owner')}>
          <Typography.Text className="text-gray" style={getEmployeeIconStyle(table)}>
            <StarIcon />
          </Typography.Text>
        </Tooltip>
      )}
      {record.authority?.executive && (
        <Tooltip title={t('executive')}>
          <Typography.Text className="text-gray" style={getEmployeeIconStyle(table)}>
            <ShieldCheckmarkIcon />
          </Typography.Text>
        </Tooltip>
      )}
    </>
  );
};

const getEmployeeIconStyle = (table?: boolean) => ({
  marginLeft: 5,
  fontSize: table ? 16 : undefined,
});

export default EmployeesTable;
